/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/**
 * NewCustomerForm.jsx
 * Created by Hassan Riaz on 1/16/20.
 */

import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'validator/es/lib/isEmpty';
import matches from 'validator/es/lib/matches';

import {
    Button,
    Checkbox,
    Cell,
    Row,
    Notification,
    CountryPicker,
    StatePicker,
    EmailField,
    PhoneNumberField,
    TextField,
    ZIPCodeField,
} from 'sarsaparilla';

export default class NewCustomerForm extends React.PureComponent {
    static propTypes = {
        createAndSelect: PropTypes.func,
        error: PropTypes.string,
    };

    constructor(props) {
        super(props);

        this.state = {
            firstName: '',
            lastName: '',
            cellPhone: '',
            email: '',
            country: 'USA',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip_code: '',
            hasNoEmail: false,
            addAddress: false,
        };
    }

    handleChange = (e) => {
        const field = e.target.name;
        const val = e.target.value;
        this.setState({
            [field]: val,
        });
    };

    handleCheckboxChange = (e) => {
        const hasNoEmail = e.target.checked;
        this.setState({
            hasNoEmail,
            email: '',
        });
    };

    handleAddressChange = (e) => {
        const addAddress = e.target.checked;
        if (!addAddress) {
            this.setState({
                country: 'USA',
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip_code: '',
            });
        }
        this.setState({
            addAddress,
        });
    };

    create = (e) => {
        e.preventDefault();
        const { addAddress, country, address1, address2, city, state, zip_code } =
            this.state;
        let hasError = false;
        if (
            isEmpty(country) ||
            isEmpty(address1) ||
            isEmpty(city) ||
            isEmpty(state) ||
            isEmpty(zip_code) ||
            (!isEmpty(city) && !matches(city, /^[a-zA-Z\- ]+$|^$/)) ||
            (!isEmpty(state) && !matches(state, /^[a-zA-Z\- ]+$|^$/))
        ) {
            hasError = true;
        }
        if (!(addAddress && hasError)) {
            this.props.createAndSelect(
                this.state.firstName,
                this.state.lastName,
                this.state.cellPhone,
                this.state.email,
                this.state.hasNoEmail,
                {
                    country,
                    address1,
                    address2,
                    city,
                    state,
                    zip_code,
                },
                addAddress
            );
        }
    };

    render() {
        let error = null;
        if (this.props.error) {
            error = (
                <div className="mb-2">
                    <Notification type="error">{this.props.error}</Notification>
                </div>
            );
        }

        let stateInput = (
            <TextField
                id="state"
                name="state"
                label="State/Province"
                value={this.state.state || ''}
                isRequired
                placeholder="Required"
                onChange={this.handleChange}
            />
        );
        let zipCodeInput = (
            <TextField
                id="zip-input"
                name="zip_code"
                label="ZIP/Postal Code"
                value={this.state.zip_code || ''}
                isRequired
                placeholder="Required"
                onChange={this.handleChange}
            />
        );
        if (this.state.country === 'USA') {
            stateInput = (
                <StatePicker
                    id="state"
                    name="state"
                    label="State"
                    value={this.state.state}
                    isRequired
                    placeholder="Required"
                    onChange={this.handleChange}
                />
            );
            zipCodeInput = (
                <ZIPCodeField
                    id="zip-input"
                    name="zip_code"
                    label="ZIP Code"
                    value={this.state.zip_code || ''}
                    isRequired
                    placeholder="Required"
                    onChange={this.handleChange}
                />
            );
        }

        return (
            <form onSubmit={this.create}>
                {error}
                <div className="mb-2">
                    <TextField
                        id="first-name"
                        label="First Name"
                        name="firstName"
                        isRequired
                        value={this.state.firstName}
                        onChange={this.handleChange}
                    />
                </div>
                <div className="mb-2">
                    <TextField
                        id="last-name"
                        label="Last Name"
                        name="lastName"
                        isRequired
                        value={this.state.lastName}
                        onChange={this.handleChange}
                    />
                </div>
                <div className="mb-2">
                    <PhoneNumberField
                        id="cell-phone"
                        label="Phone Number"
                        name="cellPhone"
                        isRequired
                        value={this.state.cellPhone}
                        onChange={this.handleChange}
                    />
                </div>
                <div className="mb-2">
                    <EmailField
                        id="email"
                        label="Email"
                        name="email"
                        value={this.state.email}
                        onChange={this.handleChange}
                        isDisabled={this.state.hasNoEmail}
                    />
                </div>
                {this.state.addAddress && (
                    <>
                        <div className="mb-2">
                            <CountryPicker
                                id="country"
                                name="country"
                                isInvalid={isEmpty(this.state.country)}
                                isRequired
                                value={this.state.country}
                                onChange={this.handleChange}
                            />
                        </div>
                        <div className="mb-2">
                            <TextField
                                id="street-input"
                                name="address1"
                                label="Street Address"
                                value={this.state.address1 || ''}
                                isRequired
                                placeholder="Required"
                                onChange={this.handleChange}
                            />
                        </div>
                        <div className="mb-2">
                            <TextField
                                id="apt-input"
                                name="address2"
                                label="Apt or Suite Number"
                                value={this.state.address2 || ''}
                                maxLength={100}
                                onChange={this.handleChange}
                            />
                        </div>
                        <div className="mb-2">
                            <TextField
                                id="city-input"
                                name="city"
                                label="City"
                                maxLength={100}
                                value={this.state.city || ''}
                                isRequired
                                placeholder="Required"
                                onChange={this.handleChange}
                            />
                        </div>
                        <div className="mb-2">
                            <Row>
                                <Cell
                                    className="acct-cell-auto"
                                    width={{ xs: 12, sm: 6 }}
                                >
                                    {stateInput}
                                </Cell>
                                <Cell
                                    className="acct-cell-auto"
                                    width={{ xs: 12, sm: 6 }}
                                >
                                    {zipCodeInput}
                                </Cell>
                            </Row>
                        </div>
                    </>
                )}
                <div className="mb-2">
                    <Checkbox
                        id="hasNoEmail"
                        label="Customer does not have an email address."
                        name="hasNoEmail"
                        value="hasNoEmail"
                        isChecked={this.state.hasNoEmail}
                        onChange={this.handleCheckboxChange}
                    />
                </div>
                <div className="mb-2">
                    <Checkbox
                        id="addAddress"
                        label="Add customer address."
                        name="addAddress"
                        value="addAddress"
                        isChecked={this.state.addAddress}
                        onChange={this.handleAddressChange}
                    />
                </div>
                <div>
                    <Button type="submit">Create and Select</Button>
                </div>
            </form>
        );
    }
}
